/**
 * Canonical artifact-title derivation.
 *
 * Single helper + three kind-specific strategies. Replaces the prior
 * fragmented behaviour where chat cards, /media library, inspector
 * headers, and project rails each derived titles inline with subtly
 * different rules.
 *
 * Schema-aware (post-2026-05-01 migration):
 *   - `social_posts.title`     — explicit user/auto title (nullable)
 *   - `media.alt_text`         — editable display name (also a11y label)
 *   - `media.prompt`           — promoted from metadata, used as fallback
 *   - `video_jobs.prompt`      — backfilled into `media.alt_text` on
 *                                completion via the
 *                                `copy_video_prompt_to_media` trigger
 *
 * Priority order (per kind):
 *
 *   image / video:
 *     1. media.alt_text          (user-editable override)
 *     2. media.prompt            (originating prompt, ~6-word truncation)
 *     3. Editorial fallback      ("Image · 1:1 · Apr 26")
 *
 *   post:
 *     1. post.title              (explicit override; user-editable)
 *     2. brand + format derive   (Lululemon · Instagram + 2 more post)
 *     3. "Untitled post"         (no platforms picked)
 *
 * Kept lean — every consumer can call `buildArtifactTitle({ kind, … })`.
 *
 * Spec: .planning/specs/2026-05-01-artifact-naming-ux.md
 */
import { platformLabel } from '@/components/ui/platform-preview';
import type { ArtifactKind } from '@/types/artifact-kind';

// =============================================================================
// Inputs
// =============================================================================

interface ImageInput {
  kind: 'image';
  altText?: string | null;
  prompt?: string | null;
  fileName?: string | null;
  aspectRatio?: string | null;
  createdAt?: string | null;
}

interface VideoInput {
  kind: 'video';
  altText?: string | null;
  prompt?: string | null;
  fileName?: string | null;
  durationSeconds?: number | null;
  aspectRatio?: string | null;
  createdAt?: string | null;
}

interface PostInput {
  kind: 'post';
  title?: string | null;
  platforms?: readonly string[] | null;
  brandKitName?: string | null;
}

export type ArtifactTitleInput = ImageInput | VideoInput | PostInput;

// =============================================================================
// Helpers
// =============================================================================

const FALLBACK_BY_KIND: Record<ArtifactKind, string> = {
  image: 'Untitled image',
  video: 'Untitled video',
  post: 'Untitled post',
};

/** Truncate to N words; trim trailing punctuation; preserve newline-free. */
export function truncateToWords(text: string, maxWords: number): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const words = cleaned.split(' ');
  if (words.length <= maxWords) return cleaned.replace(/[.,;:!?…\-—]+$/, '').trim();
  return `${words
    .slice(0, maxWords)
    .join(' ')
    .replace(/[.,;:!?\-—]+$/, '')}…`;
}

function humanShortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function nonEmptyTrim(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// An explicit title/alt-text is meant to be a short, human-scannable label —
// a real one is a handful of words. Anything past this is almost certainly
// something else entirely landing in the wrong field (e.g. a multi-paragraph
// AI-generation prompt with "Rationale:"/"Pillar:"/"Plan:" sections written
// straight into social_posts.title — see generate-content-batch's planSlotId
// insert, fixed 2026-07-27). Cap with the same truncateToWords used for
// prompt-derived fallbacks below, so a bad value degrades to the same
// canonical "…" treatment instead of dumping raw prose into every header,
// card, and rail that calls this helper.
export const EXPLICIT_TITLE_MAX_WORDS = 12;

function nonEmptyTrimCapped(value: string | null | undefined): string | null {
  const trimmed = nonEmptyTrim(value);
  return trimmed ? truncateToWords(trimmed, EXPLICIT_TITLE_MAX_WORDS) : null;
}

function buildImageTitle(input: ImageInput): string {
  const explicit = nonEmptyTrimCapped(input.altText);
  if (explicit) return explicit;
  const promptDerived = input.prompt ? nonEmptyTrim(truncateToWords(input.prompt, 6)) : null;
  if (promptDerived) return promptDerived;
  const aspect = input.aspectRatio ?? '1:1';
  const date = humanShortDate(input.createdAt);
  return date ? `Image · ${aspect} · ${date}` : `Image · ${aspect}`;
}

function buildVideoTitle(input: VideoInput): string {
  const explicit = nonEmptyTrimCapped(input.altText);
  if (explicit) return explicit;
  const promptDerived = input.prompt ? nonEmptyTrim(truncateToWords(input.prompt, 6)) : null;
  if (promptDerived) return promptDerived;
  const aspect = input.aspectRatio ?? '9:16';
  const duration = input.durationSeconds ? `${input.durationSeconds}s` : null;
  const segments = ['Video', duration, aspect].filter((s): s is string => Boolean(s));
  return segments.join(' · ');
}

function buildPostTitle(input: PostInput): string {
  const explicit = nonEmptyTrimCapped(input.title);
  if (explicit) return explicit;
  const platforms = input.platforms ?? [];
  if (platforms.length === 0) return FALLBACK_BY_KIND.post;
  const primary = platforms[0];
  if (!primary) return FALLBACK_BY_KIND.post;
  const label = platformLabel(primary);
  const extra = platforms.length - 1;
  const formatChunk = extra > 0 ? `${label} + ${extra} more post` : `${label} post`;
  const brand = nonEmptyTrim(input.brandKitName);
  return brand ? `${brand} · ${formatChunk}` : formatChunk;
}

// =============================================================================
// Public API
// =============================================================================

export function buildArtifactTitle(input: ArtifactTitleInput): string {
  switch (input.kind) {
    case 'image':
      return buildImageTitle(input);
    case 'video':
      return buildVideoTitle(input);
    case 'post':
      return buildPostTitle(input);
  }
}
